import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { tap } from 'rxjs';

import { logger } from '../telemetry/logger';

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC = /^\d+$/;
const LONG_HEX = /^[0-9a-f]{16,}$/i;

/**
 * `https://api…/api/episodes/3f2a…/nc-session-link/deliver?x=1` → `/api/episodes/:id/nc-session-link/deliver`
 *
 * Drops origin and query string and replaces id-like segments, so API events
 * group by endpoint and never carry search terms.
 */
export function apiPathTemplate(url: string): string {
  let path = url;
  try {
    path = new URL(url, location.origin).pathname;
  } catch {
    path = url.split(/[?#]/, 1)[0] ?? '';
  }
  return path
    .split('/')
    .map((segment) => (GUID.test(segment) || NUMERIC.test(segment) || LONG_HEX.test(segment) ? ':id' : segment))
    .join('/');
}

/**
 * The one central place HTTP activity is logged (components each handle their
 * own errors for the UI). One breadcrumb per call; server errors and network
 * failures also become events. 4xx stays a breadcrumb — those are expected
 * outcomes the calling component shows to the user.
 *
 * Registered first (outermost) so it sees each call's final outcome, after the
 * auth interceptor's refresh-and-retry.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  // Static assets (i18n, icons) are not API activity.
  if (!/\/api\//i.test(req.url) && !/\/hubs\//i.test(req.url)) {
    return next(req);
  }

  const method = req.method.toUpperCase();
  const path = apiPathTemplate(req.url);
  const startedAt = Date.now();

  return next(req).pipe(
    tap({
      next: (event) => {
        if (event instanceof HttpResponse) {
          logger.http({ method, path, status: event.status, durationMs: Date.now() - startedAt });
        }
      },
      error: (err: unknown) => {
        const durationMs = Date.now() - startedAt;
        const status = err instanceof HttpErrorResponse ? err.status : 0;
        logger.http({ method, path, status, durationMs });
        if (status === 0 || status >= 500) {
          const title = status === 0 ? `API ${method} ${path} failed` : `API ${method} ${path} returned ${status}`;
          logger.error(title, err instanceof HttpErrorResponse ? new Error(title) : err, { status, durationMs });
        }
      }
    })
  );
};
